// Service für Azure Booking Storage - extrahiert aus paste-5.txt
import * as XLSX from "xlsx";
import { getProjectAnalysis, getCategoryForecastAnalysis } from "./azureBudgetService";

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "bookings",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

const createUniqueKey = (nr, valuta, betrag) => {
  let dateStr;

  // Handle Excel date formats
  if (typeof valuta === "number") {
    const date = XLSX.SSF.parse_date_code(valuta);
    dateStr = `${date.y}-${String(date.m).padStart(2, "0")}-${String(
      date.d
    ).padStart(2, "0")}`;
  } else if (valuta instanceof Date) {
    dateStr = valuta.toISOString().split("T")[0];
  } else if (typeof valuta === "string") {
    // Handle different string formats
    if (valuta.includes(".")) {
      // DD.MM.YYYY format
      const parts = valuta.split(".");
      if (parts.length === 3) {
        dateStr = `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(
          2,
          "0"
        )}`;
      } else {
        dateStr = valuta; // Use as-is if already formatted
      }
    } else {
      const parsed = new Date(valuta);
      if (isNaN(parsed)) {
        dateStr = valuta; // Keep original if can't parse
      } else {
        dateStr = parsed.toISOString().split("T")[0];
      }
    }
  } else {
    dateStr = "unknown";
  }

  return `${nr}-${dateStr}-${betrag}`.replace(/[^a-zA-Z0-9-.]/g, "");
};

const getNextId = async () => {
  const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}&$filter=PartitionKey eq 'booking'&$select=RowKey`;

  try {
    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });

    if (!response.ok) return 1;

    const data = await response.json();
    const ids = data.value
      .map((item) => parseInt(item.RowKey))
      .filter((id) => !isNaN(id));
    return ids.length > 0 ? Math.max(...ids) + 1 : 1;
  } catch {
    return 1;
  }
};

const saveBooking = async (booking, rowKey) => {
  const now = new Date().toISOString();

  const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='booking',RowKey='${rowKey}')?${AZURE_CONFIG.sasToken}`;

  const entity = {
    PartitionKey: "booking",
    RowKey: rowKey,
    Nr: booking.nr || 0,
    Valuta: booking.valuta,
    Konto: booking.konto || 0,
    KontoBezeichnung: booking.kontoBezeichnung || "",
    Gegenkonto: booking.gegenkonto || "",
    GegenkontoBezeichnung: booking.gegenkontoBezeichnung || "",
    Buchungstext: booking.buchungstext || "",
    Soll: booking.soll || 0,
    Haben: booking.haben || 0,
    Betrag: booking.betrag || 0,
    Waehrung: booking.waehrung || "CHF",
    Mwst: booking.mwst || "",
    Erfassungsdatum: booking.erfassungsdatum || "",
    Erfasser: booking.erfasser || "",
    UniqueKey: booking.uniqueKey,
    CreatedAt: now,
    UpdatedAt: now,
  };

  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json;odata=nometadata",
    },
    body: JSON.stringify(entity),
  });

  if (!response.ok) {
    throw new Error(`Azure Table Error: ${response.status}`);
  }
};

const bookingExists = async (uniqueKey) => {
  const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}&$filter=PartitionKey eq 'booking' and UniqueKey eq '${uniqueKey}'`;

  try {
    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });

    if (!response.ok) return false;

    const data = await response.json();
    return data.value && data.value.length > 0;
  } catch {
    return false;
  }
};

export const uploadBookings = async (file) => {
  const workbook = XLSX.read(await file.arrayBuffer());
  const worksheet = workbook.Sheets[workbook.SheetNames[0]];
  const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

  if (data.length < 2) {
    throw new Error("Excel-Datei enthält keine Daten");
  }

  const bookings = data
    .slice(1)
    .filter((row) => row.length > 0)
    .map((row) => {
      let valuta = row[1];

      // Convert Excel date to proper format
      if (typeof valuta === "number") {
        const date = XLSX.SSF.parse_date_code(valuta);
        valuta = `${date.y}-${String(date.m).padStart(2, "0")}-${String(
          date.d
        ).padStart(2, "0")}`;
      } else if (valuta instanceof Date) {
        valuta = valuta.toISOString().split("T")[0];
      } else if (typeof valuta === "string") {
        // Handle DD.MM.YYYY format
        if (valuta.includes(".")) {
          const parts = valuta.split(".");
          if (parts.length === 3 && parts[2].length === 4) {
            valuta = `${parts[2]}-${parts[1].padStart(
              2,
              "0"
            )}-${parts[0].padStart(2, "0")}`;
          }
        } else {
          const parsed = new Date(valuta);
          if (!isNaN(parsed)) {
            valuta = parsed.toISOString().split("T")[0];
          }
        }
      }

      const booking = {
        nr: row[0],
        valuta: valuta,
        konto: row[3],
        kontoBezeichnung: row[4],
        gegenkonto: row[5],
        gegenkontoBezeichnung: row[6],
        buchungstext: row[7],
        soll: parseFloat(row[9]) || 0,
        haben: parseFloat(row[10]) || 0,
        betrag: parseFloat(row[11]) || 0,
        waehrung: row[12] || "CHF",
        mwst: row[13],
        erfassungsdatum: row[20],
        erfasser: row[21],
      };

      booking.uniqueKey = createUniqueKey(
        booking.nr,
        booking.valuta,
        booking.betrag
      );
      return booking;
    });

  let newCount = 0;
  let duplicateCount = 0;
  let currentId = await getNextId();

  const savePromises = [];

  for (const booking of bookings) {
    const exists = await bookingExists(booking.uniqueKey);

    if (exists) {
      duplicateCount++;
    } else {
      savePromises.push(saveBooking(booking, currentId.toString()));
      currentId++;
      newCount++;
    }
  }

  await Promise.all(savePromises);

  return {
    total: bookings.length,
    new: newCount,
    duplicates: duplicateCount,
  };
};

export const getBookings = async () => {
  const baseUrl = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}`;
  const filter = `&$filter=PartitionKey eq 'booking'`;

  let allBookings = [];
  let nextPartitionKey = null;
  let nextRowKey = null;
  let continuationToken = null;

  do {
    // Build URL mit Continuation Token
    let url = `${baseUrl}?${AZURE_CONFIG.sasToken}${filter}`;

    if (nextPartitionKey && nextRowKey) {
      url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
      url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
    }

    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });

    if (!response.ok) {
      throw new Error(`Azure Table Error: ${response.status}`);
    }

    const data = await response.json();
    allBookings = allBookings.concat(data.value || []);

    // Check for continuation headers
    nextPartitionKey = response.headers.get(
      "x-ms-continuation-NextPartitionKey"
    );
    nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");

    console.log(
      `Batch geladen: ${data.value?.length}, Total: ${allBookings.length}`
    );
  } while (nextPartitionKey && nextRowKey);

  console.log(`Gesamt geladen: ${allBookings.length} Buchungen`);
  return allBookings;
};

// ========== AUSWERTUNGS-FUNKTIONEN ==========

// Kategorien-Mapping
const getCategory = (konto, isIncome = false) => {
  const kontoNum = parseInt(konto);

  if (isIncome) {
    // Einnahmen-Kategorien (Gegenkonto 3000-3999)
    if (kontoNum >= 3200 && kontoNum < 3300) return "Bruttoerlöse Artikel";
    if (kontoNum >= 3400 && kontoNum < 3500)
      return "Bruttoerlöse Dienstleistung";
    if (kontoNum >= 3800 && kontoNum < 3900) return "Rabatte/Preisnachlässe";
    return "Sonstige Einnahmen";
  } else {
    // Ausgaben-Kategorien (Konto >= 4000)
    if ((kontoNum >= 5000 && kontoNum < 6000) || kontoNum === 1091)
      return "Lohn";
    if (kontoNum >= 5700 && kontoNum < 5800) return "Sozialversicherungen";
    if (kontoNum >= 5800 && kontoNum < 5900) return "Verpflegung";
    if (kontoNum >= 4000 && kontoNum < 5000) return "Material";
    if (kontoNum >= 6000 && kontoNum < 6100) return "Raumaufwand";
    if (kontoNum >= 6500 && kontoNum < 6600) return "Büromaterial";
    if (kontoNum === 6580) return "Lizenzen";
    if (kontoNum === 6900) return "Bankspesen";
    if (kontoNum >= 6700 && kontoNum < 6800) return "Abschreibungen";
    return "Sonstige Ausgaben";
  }
};

// Kategorien-Auswertung (identisch zu FinanzChart-Logik)

// Kategorien-Auswertung - OHNE DOPPLUNGEN
// Kategorien-Auswertung - MIT KREDITOR-RÜCKVERFOLGUNG
export const getCategoryAnalysis = async (
  year = new Date().getFullYear(),
  quarter = null
) => {
  const allBookings = await getBookings();

  let filteredBookings = allBookings.filter((b) => {
    const bookingDate = new Date(b.Valuta);
    const bookingYear = bookingDate.getFullYear();

    if (bookingYear !== year) return false;

    if (quarter) {
      const month = bookingDate.getMonth() + 1;
      return month >= (quarter - 1) * 3 + 1 && month <= quarter * 3;
    }

    return true;
  });

  const incomeByCategory = {};
  const expenseByCategory = {};

  // Schritt 1: Baue Mapping von Kreditor-Buchungen zu Aufwandskonten
  const kreditorToExpense = new Map();

  allBookings.forEach((b) => {
    // Finde Rechnungen: Aufwandskonto (>=4000) -> Kreditor (2000-2999)
    if (b.Konto >= 4000 && b.Gegenkonto >= 2000 && b.Gegenkonto < 3000) {
      const key = `${b.Gegenkonto}_${b.Betrag}`;
      if (!kreditorToExpense.has(key)) {
        kreditorToExpense.set(key, []);
      }
      kreditorToExpense.get(key).push({
        expenseAccount: b.Konto,
        amount: b.Betrag,
        date: b.Valuta,
      });
    }
  });

  // Schritt 2: Verarbeite Buchungen
  filteredBookings.forEach((b) => {
    if (b.Konto === b.Gegenkonto) return;

    // ==================== EINNAHMEN ====================
    if (b.Konto === 1020) {
      if (b.Gegenkonto >= 3000 && b.Gegenkonto < 4000) {
        const category = getCategory(b.Gegenkonto, true);
        incomeByCategory[category] =
          (incomeByCategory[category] || 0) + b.Betrag;
      } else if (b.Gegenkonto === 1100) {
        const category = "Kundenzahlungen";
        incomeByCategory[category] =
          (incomeByCategory[category] || 0) + b.Betrag;
      }
    }

    // ==================== AUSGABEN ====================
    else if (b.Gegenkonto === 1020) {
      let category = null;

      // Direkte Zahlung mit Aufwandskonto (4000+)
      if (b.Konto >= 4000) {
        category = getCategoryFromExpenseAccount(b.Konto);
      }
      // Kreditorenzahlung (2000-2999) -> Suche ursprüngliche Rechnung
      else if (b.Konto >= 2000 && b.Konto < 3000) {
        const key = `${b.Konto}_${b.Betrag}`;
        const matchingExpenses = kreditorToExpense.get(key);

        if (matchingExpenses && matchingExpenses.length > 0) {
          // Nimm die zeitlich nächste Rechnung vor der Zahlung
          const relevantExpense = matchingExpenses
            .filter((e) => new Date(e.date) <= new Date(b.Valuta))
            .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

          if (relevantExpense) {
            category = getCategoryFromExpenseAccount(
              relevantExpense.expenseAccount
            );
          } else {
            category = "Kreditorenzahlungen (nicht zugeordnet)";
          }
        } else {
          category = "Kreditorenzahlungen (nicht zugeordnet)";
        }
      }
      // Lohndurchlauf oder andere Bilanzkonten
      else if (b.Konto === 1091) {
        category = "Lohn";
      } else if (b.Konto >= 1000 && b.Konto < 2000) {
        category = "Sonstige Zahlungen";
      }

      if (category) {
        expenseByCategory[category] =
          (expenseByCategory[category] || 0) + b.Betrag;
      }
    }
  });

  return {
    year,
    quarter,
    income: {
      total: Object.values(incomeByCategory).reduce((sum, val) => sum + val, 0),
      byCategory: incomeByCategory,
    },
    expense: {
      total: Object.values(expenseByCategory).reduce(
        (sum, val) => sum + val,
        0
      ),
      byCategory: expenseByCategory,
    },
  };
};

// Hilfsfunktion: Kategorisierung nach Aufwandskonto
const getCategoryFromExpenseAccount = (konto) => {
  const kontoNum = parseInt(konto);

  if (kontoNum >= 4000 && kontoNum < 5000) return "Material";
  if (kontoNum >= 5700 && kontoNum < 5800) return "Sozialversicherungen";
  if (kontoNum >= 5800 && kontoNum < 5900) return "Verpflegung";
  if (kontoNum >= 5000 && kontoNum < 6000) return "Lohn";
  if (kontoNum >= 6000 && kontoNum < 6100) return "Raumaufwand";
  if (kontoNum >= 6500 && kontoNum < 6600 && kontoNum !== 6580)
    return "Büromaterial";
  if (kontoNum === 6580) return "Lizenzen";
  if (kontoNum === 6900) return "Bankspesen";
  if (kontoNum >= 6000) return "Sonstige Ausgaben";

  return "Sonstige Ausgaben";
};

// Komplett-Auswertung (ohne Projekte)
export const getComprehensiveAnalysis = async (year, quarter = null) => {
  const [categoryAnalysis, projectAnalysis, forecastAnalysis] =
    await Promise.all([
      getCategoryAnalysis(year, quarter),
      getProjectAnalysis(year, quarter),
      getCategoryForecastAnalysis(year, quarter),
    ]);

  return {
    year,
    quarter,
    categories: categoryAnalysis,
    projects: projectAnalysis,
    forecasts: forecastAnalysis,
  };
};
